import type { BondTypeCode, FeatureCode } from "@/shared/types/bondTypes";
import type { BondSectorCode } from "@/shared/types/bondSectors";
import type { BondAttributeCode } from "@/shared/types/bondAttributes";
import type { IncludeExcludeFilter } from "@/shared/types/includeExcludeFilter";
import type { RateStructureCode } from "@/shared/types/bondRateStructures";
import type { RedemptionStatusCode } from "@/shared/types/bondRedemptionStatuses";
import type { TradeTypeCode } from "@/shared/types/trade";
import type { TradeDateRangePreset, TradeSizePreset } from "@/shared/types/tradePresets";

/**
 * Context in which Advanced Search is opened. Controls which sections and
 * individual fields are visible.
 */
export type FilterContext = "markets-quotes" | "markets-rfqs" | "watchlist";

export type MarketTab = "quotes" | "rfqs" | "history" | "messages";

/** How Moody's and S&P min floors combine when both are set (ignored when only one is set). */
export type RatingsCombineMode = "AND" | "OR";

// region: AdvancedSearch — Numeric Filters (FE-only, awaiting backend support)
// Copy this whole region to share with backend for API contract additions.

/** Benchmark used for spread comparison. */
export type SpreadBenchmark = "aaa" | "treasury";
/** Default spread benchmark when none is selected. */
export const DEFAULT_SPREAD_BENCHMARK: SpreadBenchmark = "treasury";

/** What kind of difference-from-evaluation to filter on. */
export type DiffToEvalMode = "yield" | "price";
/** Default diff-to-eval mode when none is selected. */
export const DEFAULT_DIFF_TO_EVAL_MODE: DiffToEvalMode = "price";

/** Which duration measure to filter on. */
export type DurationType = "modified" | "effective" | "oas";
/** Default duration measure when none is selected. */
export const DEFAULT_DURATION_TYPE: DurationType = "modified";

/** Which date/range axis the Workout Dates filter applies to. OID has its own oidMode/oidMin/oidMax keys. */
export type RangeType = "convertDate" | "tenderPutDate" | "refunding";
/** Default range axis when none is selected. */
export const DEFAULT_RANGE_TYPE: RangeType = "convertDate";

/** Whether OID is measured as a yield spread or price differential. */
export type OidMode = "yield" | "price";
/** Default OID measurement mode. */
export const DEFAULT_OID_MODE: OidMode = "yield";

// endregion: AdvancedSearch — Numeric Filters

// region: AdvancedSearch — Bond Type and Features (backend-backed)
// Sent as the `bondType` and `features` IncludeExcludeFilters and the nullable
// `isDoubleBarreled` flag by the helpers in bondTypeWireFilters.ts.

/** Included Bond Types (OR semantics when multiple). Query: bondTypesIncluded=go_utgo&bondTypesIncluded=revenue */
export type BondTypesIncluded = BondTypeCode[];
/** Excluded Bond Types (OR semantics when multiple). Query: bondTypesExcluded=certificates_of_participation */
export type BondTypesExcluded = BondTypeCode[];

/** Included Features (match any listed). Query: featuresIncluded=babs&featuresIncluded=special_assessment */
export type FeaturesIncluded = FeatureCode[];
/** Excluded Features (hide any match). Query: featuresExcluded=notes_short_term */
export type FeaturesExcluded = FeatureCode[];

// endregion: AdvancedSearch — Bond Type and Features

// region: AdvancedSearch — Sector

/** Selected sectors (OR semantics when multiple). Query: sectors=Airport&sectors=Hospitals */
export type Sectors = BondSectorCode[];

// endregion: AdvancedSearch — Sector

// region: AdvancedSearch — Attributes (partially backend-backed)
// Codes with a dedicated backend boolean filter (isInsured, isPutable, ...) are
// mapped by appendAttributeBoolFilters in queryParams.ts; the remaining codes
// are no-ops until backend support exists.

/** Included bond attributes (AND semantics when multiple — all must apply). Query: attributesIncluded=insured&attributesIncluded=putable */
export type AttributesIncluded = BondAttributeCode[];
/** Excluded bond attributes (OR semantics when multiple — hide any match). Query: attributesExcluded=escrowed */
export type AttributesExcluded = BondAttributeCode[];

// endregion: AdvancedSearch — Attributes

// region: AdvancedSearch — Rate Structure & Redemption Status (backend-backed via IncludeExcludeFilter)

/** Rate structure filter. Mirrors backend RateStructures: IncludeExcludeFilter<string>. */
export type RateStructuresFilter = IncludeExcludeFilter<RateStructureCode>;

/** Redemption status filter. Mirrors backend RedemptionStatuses: IncludeExcludeFilter<string>. */
export type RedemptionStatusesFilter = IncludeExcludeFilter<RedemptionStatusCode>;

// endregion: AdvancedSearch — Rate Structure & Redemption Status

/**
 * Maturity filter input mode. "year"/"date" are absolute bounds; "years" and
 * "days" are relative offsets from today, resolved to absolute dates at query
 * time so saved searches stay "N years/days out" whenever they run.
 */
export type MaturityMode = "year" | "date" | "years" | "days";
/** Default maturity mode when none is selected. */
export const DEFAULT_MATURITY_MODE: MaturityMode = "year";

/** Resolve the effective maturity mode, falling back to the default. */
export function resolveMaturityMode(filters: MarketFilters): MaturityMode {
  return filters.maturity?.mode ?? DEFAULT_MATURITY_MODE;
}

/** Quote side filter — "Buy" (Bids) or "Sell" (Offers). Omit for full depth. */
export type SideFilter = "Buy" | "Sell";

/** Normalized asset-type classification. Mirrors the backend AssetType enum. */
export type AssetTypeCode = "Muni" | "Corporate" | "Government" | "Sovereign" | "Unknown";

/** Inclusive min/max range. Mirrors the backend Range<T> contract. */
export interface Range<T> {
  min?: T;
  max?: T;
}

/**
 * Maturity date-range filter. `mode` selects which sub-range is active;
 * the others are retained so switching modes in the UI doesn't lose data.
 */
export interface MaturityFilter {
  mode?: MaturityMode;
  year?: Range<number>; // "year" mode (default)
  date?: Range<string>; // "date" mode — ISO date strings
  yearsOut?: Range<number>; // "years" mode — years from today (decimals allowed)
  days?: Range<number>; // "days" mode — calendar days from today
}

/** Callable (next call date) range filter. */
export interface CallableFilter {
  /**
   * Active input mode. When omitted, falls back to `useFullDate` (legacy URLs)
   * then to `"year"`.
   */
  mode?: MaturityMode;
  year?: Range<number>;
  nextCallDate?: Range<string>; // "date" mode — ISO date strings
  yearsOut?: Range<number>; // "years" mode — years from today
  days?: Range<number>; // "days" mode — calendar days from today
  // Legacy: prefer `mode`; kept for URL / saved-search backward compat.
  useFullDate?: boolean;
  excludeExtraordinary?: boolean; // sent as `excludeExtraordinaryRedemption` on the API
}

/**
 * Resolve the effective callable mode.
 * Prefer explicit `mode`, then legacy `useFullDate`, then infer from which
 * range fields are populated (older URLs / saved searches).
 */
export function resolveCallableMode(filters: MarketFilters): MaturityMode {
  const c = filters.callable;
  if (c?.mode) return c.mode;
  if (c?.useFullDate !== undefined) return c.useFullDate ? "date" : "year";
  if (c?.nextCallDate?.min != null || c?.nextCallDate?.max != null) return "date";
  if (c?.yearsOut?.min != null || c?.yearsOut?.max != null) return "years";
  if (c?.days?.min != null || c?.days?.max != null) return "days";
  return DEFAULT_MATURITY_MODE;
}

/**
 * Tax-equivalent yield composite filter. The federal rate is required for the
 * filter to be considered active; all other fields are optional enhancements.
 */
export interface TeyFilter {
  federalRate?: number; // Federal tax rate as a percentage (0–99)
  stateRate?: number; // State tax rate as a percentage (0–99)
  userState?: string; // User's state of residence (US state code, e.g. "NY")
  range?: Range<number>; // Min/max tax-equivalent yield bounds
}

export interface MarketFilters {
  tab: MarketTab;
  side?: SideFilter;
  /** MSRB trade type. History tab only. Omit for every type. */
  tradeType?: TradeTypeCode;
  /** Rolling trade-date window. History tab only. Omit for today. */
  tradeDateRange?: TradeDateRangePreset;
  /** Minimum par preset. History tab only. Omit for every size. */
  tradeSize?: TradeSizePreset;
  assetType?: AssetTypeCode[];

  // Bond property filters (shared across tabs)
  quantity?: Range<number>;
  yieldToWorst?: Range<number>;
  tey?: TeyFilter;
  yieldToMaturity?: Range<number>;
  coupon?: Range<number>;
  maturity?: MaturityFilter;
  callable?: CallableFilter;
  dollarPrice?: Range<number>;
  states?: string[]; // US state codes (e.g. "NY", "NJ"). Sent as `stateCode` to both Quotes and RFQ search.
  // Tax Status (Advanced Search) — independent tri-state booleans; absent = no constraint.
  isFederallyTaxable?: boolean; // Sent as `isFederallyTaxable`; backend maps to SecurityIsTaxable.
  isStateTaxable?: boolean; // Sent as `isStateTaxable`; backend maps to SecurityIsStateTaxable.
  isAmt?: boolean; // Sent as `isAmt`; backend maps to SecurityIsAmt.
  isBankQualified?: boolean; // Sent as `isBankQualified`; backend maps to SecurityIsBankQualified.
  issuer?: string[]; // CUSIP6 prefix filter (first 6 chars = issuer)
  moodysMinRating?: string; // NOT implemented on backend yet
  spMinRating?: string; // NOT implemented on backend yet
  ratingsCombine?: RatingsCombineMode; // NOT implemented on backend yet
  ratingsUseUnderlyingOnly?: boolean; // NOT implemented on backend yet
  marketDate?: string;
  includeHistorical?: boolean;
  showUnknownCusips?: boolean;

  // region: AdvancedSearch — Numeric Filters (FE-only, awaiting backend support)
  // Copy this whole region to share with backend for API contract additions.
  spreadType?: SpreadBenchmark; // default "treasury"
  spread?: Range<number>; // bps
  diffToEvalMode?: DiffToEvalMode; // default "price"
  diffToEval?: Range<number>; // bps when yield, $ when price
  durationType?: DurationType; // default "modified"
  duration?: Range<number>; // years
  rangeType?: RangeType; // default "convertDate"
  range?: Range<string>; // date variants -> YYYY (OID moved to oid)
  issuers?: string[]; // contains-match, OR'd
  obligors?: string[]; // contains-match, OR'd
  oidMode?: OidMode; // default "yield"; bps when yield, $ when price
  oid?: Range<number>; // NOT implemented on backend yet
  maturitySize?: Range<number>; // $M — sent as `offeringAmount.min`/`.max`; size of this specific maturity/CUSIP
  dealSize?: Range<number>; // $M — sent as `dealSize.min`/`.max`; total size of the deal this maturity belongs to (S&P-sourced only)
  // Conditional date ranges — shown in UI when the corresponding attribute is included
  preRefundedDate?: Range<string>; // NOT implemented on backend yet
  // endregion: AdvancedSearch — Numeric Filters

  // region: AdvancedSearch — Bond Type and Features (backend-backed; see bondTypeWireFilters.ts)
  bondTypesIncluded?: BondTypesIncluded; // OR within list; AND with bondTypesExcluded when both set
  bondTypesExcluded?: BondTypesExcluded; // hide bonds matching any listed type
  featuresIncluded?: FeaturesIncluded; // match any listed feature
  featuresExcluded?: FeaturesExcluded; // hide bonds carrying any listed feature
  isDoubleBarreled?: boolean; // true = only double-barreled, false = hide them; absent = no constraint
  // endregion: AdvancedSearch — Bond Type and Features

  // region: AdvancedSearch — Sector
  sectors?: Sectors; // OR within list
  // endregion: AdvancedSearch — Sector

  // region: AdvancedSearch — Attributes (partially backend-backed; see appendAttributeBoolFilters)
  attributesIncluded?: AttributesIncluded; // AND within list; bond must carry every listed attribute
  attributesExcluded?: AttributesExcluded; // hide bonds carrying any listed attribute
  // endregion: AdvancedSearch — Attributes

  // region: AdvancedSearch — Identifiers
  cusips?: string[]; // Full CUSIP9 lookup. Maps to SecurityIdentifiers on the API.
  // endregion: AdvancedSearch — Identifiers

  // region: AdvancedSearch — Rate Structure & Redemption Status (backend-backed)
  rateStructures?: RateStructuresFilter; // maps to RateStructures: IncludeExcludeFilter<string>
  redemptionStatuses?: RedemptionStatusesFilter; // maps to RedemptionStatuses: IncludeExcludeFilter<string>
  // endregion: AdvancedSearch — Rate Structure & Redemption Status

  // Sorting
  sortBy?: string;
  sortDesc?: boolean;
}

export const DEFAULT_MARKET_FILTERS: MarketFilters = {
  tab: "quotes",
  side: "Sell",
  assetType: ["Muni"],
};

/** Keys that only apply to the Quotes tab -- cleared on tab switch to RFQs. */
export const QUOTE_ONLY_KEYS: (keyof MarketFilters)[] = ["side"];

/** Keys that only apply to the RFQs tab -- cleared on tab switch to Quotes. */
export const RFQ_ONLY_KEYS: (keyof MarketFilters)[] = [];

/** Tabs backed by `MarketFilters` (everything except Messages, which uses its own shape). */
export type MarketFilterTab = "quotes" | "rfqs" | "history";

/**
 * Per-filter tab applicability. A key listed here only applies to the listed
 * tab(s); any key NOT listed applies to ALL tabs. This is the single source of
 * truth that drives the unified Markets filter state: filters that don't apply
 * to the active tab are masked out of that tab's URL/results but stay stashed in
 * the canonical state so they return when the user revisits a tab where they
 * apply. To add a new tab-specific filter, add one entry below.
 */
export const FILTER_TAB_RESTRICTIONS: Partial<
  Record<keyof MarketFilters, readonly MarketFilterTab[]>
> = {
  // History reuses the Quotes filter bar and Advanced Search dialog as-is (see
  // MarketsPageClient), so it shares these restrictions rather than getting its own.
  side: ["quotes", "history"],
  yieldToWorst: ["quotes", "history"],
  tey: ["quotes", "history"],
  yieldToMaturity: ["quotes", "history"],
  tradeType: ["history"],
  tradeDateRange: ["history"],
  tradeSize: ["history"],
};

export const SearchType = {
  Markets: 0,
  RFQs: 1,
  TradeHistory: 2,
} as const;

export type SearchType = (typeof SearchType)[keyof typeof SearchType];

export const SEARCH_TYPE_LABEL: Record<number, string> = {
  [SearchType.Markets]: "Markets",
  [SearchType.RFQs]: "RFQs",
  [SearchType.TradeHistory]: "Trade History",
};

/** Shape returned by the saved-searches backend API. */
export interface SavedSearchDto {
  id: string;
  name: string;
  searchType: number;
  criteria: string;
  createdAt: string;
  updatedAt: string;
}

/** Client-side representation with the criteria already deserialized. */
export interface SavedSearch {
  id: string;
  name: string;
  searchType: number;
  filters: MarketFilters;
  createdAt: string;
  updatedAt: string;
  /** Set when `criteria` was not valid JSON. `filters` is empty until the search is saved again. */
  criteriaUnreadable?: boolean;
}
